/* ============================================================
   NIYAMA WELLNESS — Product Quiz Logic
   Scoring engine, Klaviyo integration, results renderer
   ============================================================ */

(function () {
  'use strict';

  // ── Bundle IDs (must match schema block IDs) ──────────────
  const BUNDLES = {
    DEEP_SLEEP_DUO:      'deep_sleep_duo',
    DEEP_SLEEP_RITUAL:   'deep_sleep_ritual',
    HAPPY_GUT:           'happy_gut_healthy_glow',
    GOOD_MORNING:        'good_morning_essentials',
    MIDLIFE:             'mid_life_management',
    ULTIMATE:            'ultimate_kit',
  };

  // ── Add-on IDs ────────────────────────────────────────────
  const ADDONS = {
    HEY_RELAX:   'addon_hey_relax',
    SLB_CAPS:    'addon_slb_capsules',
    YOU_VE_GUT:  'addon_youve_gut_this',
    BLOATY:      'addon_bloaty',
    BATH_SOAK:   'addon_bath_soak',
  };

  // ── Scoring matrix ─────────────────────────────────��─���────
  // Q1: age  Q2: challenge  Q3: time  Q4: goal
  // Each answer adds points to bundle scores

  function calculateScores(answers) {
    const scores = {};
    Object.values(BUNDLES).forEach(id => scores[id] = 0);

    const { age, challenge, timing, goal } = answers;

    // Q1 — Age range
    if (age === '35_44') {
      scores[BUNDLES.MIDLIFE]       += 1;
      scores[BUNDLES.GOOD_MORNING]  += 1;
    }
    if (age === '45_plus') {
      scores[BUNDLES.MIDLIFE]       += 3;
      scores[BUNDLES.DEEP_SLEEP_DUO]    += 1;
      scores[BUNDLES.DEEP_SLEEP_RITUAL] += 1;
    }

    // Q2 — Biggest challenge
    if (challenge === 'sleep') {
      scores[BUNDLES.DEEP_SLEEP_DUO]    += 3;
      scores[BUNDLES.DEEP_SLEEP_RITUAL] += 3;
    }
    if (challenge === 'gut_skin') {
      scores[BUNDLES.HAPPY_GUT]    += 4;
    }
    if (challenge === 'energy_nutrition') {
      scores[BUNDLES.GOOD_MORNING] += 4;
      scores[BUNDLES.MIDLIFE]      += 1;
    }
    if (challenge === 'all') {
      scores[BUNDLES.ULTIMATE]     += 6;
      scores[BUNDLES.MIDLIFE]      += 2;
      scores[BUNDLES.GOOD_MORNING] += 1;
      scores[BUNDLES.HAPPY_GUT]    += 1;
    }

    // Q3 — Time of day
    if (timing === 'morning') {
      scores[BUNDLES.GOOD_MORNING] += 2;
      scores[BUNDLES.MIDLIFE]      += 1;
    }
    if (timing === 'evening') {
      scores[BUNDLES.DEEP_SLEEP_DUO]    += 2;
      scores[BUNDLES.DEEP_SLEEP_RITUAL] += 3;
    }
    if (timing === 'both') {
      scores[BUNDLES.ULTIMATE]     += 2;
      scores[BUNDLES.MIDLIFE]      += 2;
      scores[BUNDLES.DEEP_SLEEP_RITUAL] += 1;
      scores[BUNDLES.GOOD_MORNING] += 1;
    }

    // Q4 — Goal
    if (goal === 'feel_better') {
      scores[BUNDLES.DEEP_SLEEP_DUO] += 1;
      scores[BUNDLES.HAPPY_GUT]      += 1;
    }
    if (goal === 'look_feel') {
      scores[BUNDLES.HAPPY_GUT]    += 3;
      scores[BUNDLES.MIDLIFE]      += 1;
    }
    if (goal === 'perform_recover') {
      scores[BUNDLES.GOOD_MORNING] += 3;
      scores[BUNDLES.MIDLIFE]      += 2;
    }
    if (goal === 'total_overhaul') {
      scores[BUNDLES.ULTIMATE]     += 5;
    }

    // ── Ultimate Kit override rule ────────────────────────
    // If 3+ distinct bundle categories score > 2, push Ultimate
    const highScorers = Object.entries(scores)
      .filter(([id, s]) => id !== BUNDLES.ULTIMATE && s >= 2).length;
    if (highScorers >= 3) {
      scores[BUNDLES.ULTIMATE] += 4;
    }

    return scores;
  }

  // ── Determine top 3 recommendations ──────────��───────────
  function getRecommendations(scores) {
    const sorted = Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .filter(([, s]) => s > 0);

    const primary = sorted[0]?.[0];

    // If Ultimate is primary, show it + next 2 sub-bundles
    if (primary === BUNDLES.ULTIMATE) {
      const subs = sorted
        .filter(([id]) => id !== BUNDLES.ULTIMATE)
        .slice(0, 2)
        .map(([id]) => id);
      return [primary, ...subs];
    }

    return sorted.slice(0, 3).map(([id]) => id);
  }

  // ── Add-on logic ──────────────────────────────────────────
  function getAddon(answers, primaryBundle) {
    const { challenge, timing } = answers;

    // Ultimate Kit needs no add-on
    if (primaryBundle === BUNDLES.ULTIMATE) return null;

    if (primaryBundle === BUNDLES.DEEP_SLEEP_DUO || primaryBundle === BUNDLES.DEEP_SLEEP_RITUAL) {
      if (challenge === 'gut_skin') return ADDONS.YOU_VE_GUT;
      if (timing === 'morning')    return ADDONS.BLOATY;
      return null;
    }

    if (primaryBundle === BUNDLES.GOOD_MORNING) {
      // They've covered morning — suggest evening wind-down
      return ADDONS.HEY_RELAX;
    }

    if (primaryBundle === BUNDLES.HAPPY_GUT) {
      if (challenge === 'sleep' || timing === 'evening') return ADDONS.SLB_CAPS;
      return null;
    }

    if (primaryBundle === BUNDLES.MIDLIFE) {
      if (timing === 'evening' || challenge === 'sleep') return ADDONS.BATH_SOAK;
      return null;
    }

    return null;
  }

  // ── Klaviyo integration ───────────────────────────────────
  async function pushToKlaviyo(publicKey, listId, userData, answers, recommendations) {
    if (!publicKey || !listId) return;

    const { email, firstName } = userData;
    const [primary, secondary, tertiary] = recommendations;

    try {
      // 1. Identify profile with properties
      await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + publicKey, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2023-12-15',
        },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
              first_name: firstName,
              properties: {
                quiz_age_range:               answers.age,
                quiz_biggest_challenge:       answers.challenge,
                quiz_time_priority:           answers.timing,
                quiz_goal:                    answers.goal,
                quiz_primary_recommendation:  primary   || '',
                quiz_secondary_recommendation: secondary || '',
                quiz_tertiary_recommendation: tertiary  || '',
                quiz_completed_date:          new Date().toISOString(),
              },
            },
          },
        }),
      });

      // 2. Subscribe to list
      await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${publicKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2025-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Product Quiz',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        }),
      });

      // 3. Track event
      await fetch('https://a.klaviyo.com/client/events/?company_id=' + publicKey, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: 'Completed Product Quiz' } } },
              profile: { data: { type: 'profile', attributes: { email, first_name: firstName } } },
              properties: {
                age_range:               answers.age,
                biggest_challenge:       answers.challenge,
                time_priority:           answers.timing,
                goal:                    answers.goal,
                primary_recommendation:  primary   || '',
                secondary_recommendation: secondary || '',
                tertiary_recommendation: tertiary  || '',
              },
              time: new Date().toISOString(),
            },
          },
        }),
      });

    } catch (e) {
      console.warn('Klaviyo push failed:', e);
      // Non-blocking — quiz continues regardless
    }
  }

  // ── Shopify customer storage (no backend — storefront customer form) ──
  function pushToShopify(userData, answers, recommendations) {
    var email = (userData.email || '').trim();
    if (!email) return;
    var slug = function (s) {
      return String(s == null ? '' : s).toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    };
    var tags = ['quiz-taker'];
    if (answers.age)        tags.push('quiz-age-' + slug(answers.age));
    if (answers.challenge)  tags.push('quiz-challenge-' + slug(answers.challenge));
    if (answers.timing)     tags.push('quiz-time-' + slug(answers.timing));
    if (answers.goal)       tags.push('quiz-goal-' + slug(answers.goal));
    if (recommendations[0]) tags.push('quiz-rec-' + slug(recommendations[0]));

    var note = 'Product Quiz ' + new Date().toISOString().slice(0, 10) +
      ' — age: ' + (answers.age || '-') +
      ', challenge: ' + (answers.challenge || '-') +
      ', priority: ' + (answers.timing || '-') +
      ', goal: ' + (answers.goal || '-') +
      '. Recommended: ' + recommendations.join(', ') + '.';

    var body = new URLSearchParams();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    if (userData.firstName) body.append('contact[first_name]', userData.firstName);
    body.append('contact[tags]', tags.join(','));
    body.append('contact[note]', note); // best-effort; storefront form may ignore note

    fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    }).catch(function (e) { console.warn('Shopify customer push failed:', e); });
  }

  // ── Quiz state ────────────────────────────────────────────
  let answers  = {};
  let userData = {};

  // ── DOM helpers ───────────────────────────────────────────
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }

  function showStep(quizEl, step) {
    qsa('.nq-step', quizEl).forEach(el => el.classList.remove('nq-step--active'));
    const target = qs(`[data-step="${step}"]`, quizEl);
    if (target) {
      target.classList.add('nq-step--active');
      quizEl.dataset.currentStep = step;
    }
  }

  function updateProgress(quizEl, step) {
    const bar   = qs('.nq-progress__fill', quizEl);
    const label = qs('.nq-progress__label', quizEl);
    const total = 4;
    if (bar)   bar.style.width = `${(step / total) * 100}%`;
    if (label) label.textContent = `${step} of ${total}`;
  }

  // ── Build results HTML ────────────────────────────────────
  function buildResults(quizEl, recommendations, addonId) {
    const firstName  = userData.firstName || '';
    const headlinePrefix = quizEl.dataset.resultsPrefix || 'Your recommendations';
    const resultsTitle   = firstName
      ? `${firstName}'s recommendations`
      : headlinePrefix;

    const titleEl = qs('.nq-results__title', quizEl);
    if (titleEl) titleEl.textContent = resultsTitle;

    const container = qs('.nq-results__bundles', quizEl);
    if (!container) return;
    container.innerHTML = '';

    recommendations.forEach((bundleId, idx) => {
      const block = qs(`[data-bundle-id="${bundleId}"]`, quizEl);
      if (!block) return;

      const name     = block.dataset.bundleName     || '';
      const headline = block.dataset.bundleHeadline || '';
      const outcomes = [
        block.dataset.bundleOutcome1,
        block.dataset.bundleOutcome2,
        block.dataset.bundleOutcome3,
      ].filter(Boolean);
      const ctaLabel = block.dataset.bundleCtaLabel || 'Shop now';
      const ctaLink  = block.dataset.bundleCtaLink  || '#';
      const image    = block.dataset.bundleImage    || '';
      const isPrimary = idx === 0;

      const card = document.createElement('div');
      card.className = `nq-bundle-card ${isPrimary ? 'nq-bundle-card--primary' : ''}`;
      card.innerHTML = `
        ${isPrimary ? '<span class="nq-bundle-card__badge">Best match</span>' : ''}
        ${image ? `<div class="nq-bundle-card__image"><img src="${image}" alt="${name}" loading="lazy"></div>` : ''}
        <div class="nq-bundle-card__body">
          <h3 class="nq-bundle-card__name">${name}</h3>
          <p class="nq-bundle-card__headline">${headline}</p>
          ${outcomes.length ? `
            <ul class="nq-bundle-card__outcomes">
              ${outcomes.map(o => `<li>${o}</li>`).join('')}
            </ul>` : ''}
          <a href="${ctaLink}" class="nq-btn nq-btn--primary">${ctaLabel}</a>
        </div>
      `;
      container.appendChild(card);
    });

    // ── Add-on ────────────────────────────────────────────
    const addonSection = qs('.nq-results__addon', quizEl);
    if (addonSection) {
      if (addonId) {
        const addonBlock = qs(`[data-addon-id="${addonId}"]`, quizEl);
        if (addonBlock) {
          const aName    = addonBlock.dataset.addonName    || '';
          const aHead    = addonBlock.dataset.addonHeadline || '';
          const aOutcome = addonBlock.dataset.addonOutcome  || '';
          const aCta     = addonBlock.dataset.addonCtaLabel || 'Add to routine';
          const aLink    = addonBlock.dataset.addonCtaLink  || '#';
          const aImage   = addonBlock.dataset.addonImage    || '';

          const addonCard = qs('.nq-addon-card', addonSection);
          if (addonCard) {
            addonCard.innerHTML = `
              ${aImage ? `<div class="nq-addon-card__image"><img src="${aImage}" alt="${aName}" loading="lazy"></div>` : ''}
              <div class="nq-addon-card__body">
                <span class="nq-addon-card__label">Complete your routine</span>
                <h3 class="nq-addon-card__name">${aName}</h3>
                <p class="nq-addon-card__headline">${aHead}</p>
                ${aOutcome ? `<p class="nq-addon-card__outcome">${aOutcome}</p>` : ''}
                <a href="${aLink}" class="nq-btn nq-btn--secondary">${aCta}</a>
              </div>
            `;
          }
          addonSection.style.display = '';
        } else {
          addonSection.style.display = 'none';
        }
      } else {
        addonSection.style.display = 'none';
      }
    }
  }

  // ── Init each quiz on the page ────────────────────────────
  function initQuiz(quizEl) {
    const publicKey = quizEl.dataset.klaviyoKey  || '';
    const listId    = quizEl.dataset.klaviyoList || '';

    showStep(quizEl, 1);
    updateProgress(quizEl, 1);

    // ── Answer selection ──────────────────────────────────
    quizEl.addEventListener('click', function (e) {
      const answerBtn = e.target.closest('[data-answer]');
      if (!answerBtn) return;

      const step      = parseInt(quizEl.dataset.currentStep, 10);
      const questionKey = answerBtn.closest('.nq-step').dataset.questionKey;
      const value     = answerBtn.dataset.answer;

      // Highlight selected
      qsa('[data-answer]', answerBtn.closest('.nq-step')).forEach(btn => {
        btn.classList.remove('nq-answer--selected');
      });
      answerBtn.classList.add('nq-answer--selected');

      answers[questionKey] = value;

      // Slight delay for visual feedback then advance
      setTimeout(() => {
        if (step < 4) {
          showStep(quizEl, step + 1);
          updateProgress(quizEl, step + 1);
        } else {
          // Final question answered — show email gate
          showStep(quizEl, 'email');
        }
      }, 280);
    });

    // ── Email gate form ───────────────────────────────────
    const emailForm = qs('.nq-email-form', quizEl);
    if (emailForm) {
      emailForm.addEventListener('submit', async function (e) {
        e.preventDefault();
        const emailInput = qs('[name="email"]', emailForm);
        const nameInput  = qs('[name="first_name"]', emailForm);
        userData.email      = emailInput?.value?.trim() || '';
        userData.firstName  = nameInput?.value?.trim()  || '';
        await submitAndShow(quizEl, publicKey, listId);
      });
    }

    // ── Skip button ───────────────────────────────────────
    const skipBtn = qs('.nq-skip-btn', quizEl);
    if (skipBtn) {
      skipBtn.addEventListener('click', async function () {
        userData.email     = '';
        userData.firstName = '';
        await submitAndShow(quizEl, publicKey, listId);
      });
    }

    // ── Restart ───────────────────────────────────────────
    quizEl.addEventListener('click', function (e) {
      if (!e.target.closest('.nq-restart-btn')) return;
      answers  = {};
      userData = {};
      showStep(quizEl, 1);
      updateProgress(quizEl, 1);
    });
  }

  async function submitAndShow(quizEl, publicKey, listId) {
    const scores          = calculateScores(answers);
    const recommendations = getRecommendations(scores);
    const addonId         = getAddon(answers, recommendations[0]);

    // Push to Klaviyo (non-blocking)
    if (userData.email) {
      pushToKlaviyo(publicKey, listId, userData, answers, recommendations);
      pushToShopify(userData, answers, recommendations);
    }

    buildResults(quizEl, recommendations, addonId);
    showStep(quizEl, 'results');

    // Smooth scroll to results
    quizEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Boot ──────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    qsa('.niyama-quiz').forEach(initQuiz);
  });

})();
